import type { AssetDescription } from '@play-co/timestep-core';
import type { ThemeAsset } from 'src/game/theme/Theme';
import type { DreamDriftTheme } from 'src/replicant/dreamDrift/dreamDrift.ruleset';
import { dreamDriftGetEventTheme } from 'src/replicant/dreamDrift/dreamDrift.getters';
import { now } from 'src/StateObserver';
import type { DreamDriftNodeStatus } from './components/DreamDriftNode';
import { dreamDriftThemeAsiaTravel } from './themes/dreamDrift.theme.asiatravel';
import { dreamDriftThemeMomotaro } from './themes/dreamDrift.theme.momotaro';
import { dreamDriftThemeUrashima } from './themes/dreamDrift.theme.urashima';

/** Source-coordinate position for one Dream Drift progression node. */
type DreamDriftMapNodePosition = {
  x: number;
  y: number;
};

/** Description used to reconstruct one themed Dream Drift progression map. */
export type DreamDriftMapConfig = {
  dimensions: {
    width: number;
    height: number;
  };
  numSlices: number;
  order: string[];
  /** Static chest artwork positioned by its top-left corner in source map pixels. */
  chests: {
    /** Filename resolved through the theme's map image folder. */
    image: string;
    /** Horizontal source-coordinate position of the chest. */
    x: number;
    /** Vertical source-coordinate position of the chest. */
    y: number;
    /** Draw order relative to progression nodes and map decorations. */
    zIndex: number;
  }[];
  chests12: DreamDriftMapConfig['chests'];
  /** Shared Spine export and placements, or null for a fully static map. */
  spine: {
    /** Extensionless path loaded before any map animation is constructed. */
    path: string;
    /** Independent looping characters, each using its own skeleton instance. */
    animations: {
      /** Animation name authored in the shared Spine export. */
      id: string;
      /** Horizontal position of the animation origin in source map pixels. */
      x: number;
      /** Vertical position of the animation origin in source map pixels. */
      y: number;
      /** Artwork scale relative to the source map. */
      scale: number;
      /** Mirror the character to preserve its facing in the map composition. */
      flipX?: boolean;
    }[];
  } | null;
  nodes: DreamDriftMapNodePosition[];
  nodes12: DreamDriftMapNodePosition[];
};

/** Animated or static HUD artwork and the themed level-number layout. */
export type DreamDriftHudButtonAssets = {
  timer: string;
  /** Number center in HUD-container coordinates at game resolution, with rotation in radians. */
  levelLabelLayout: { x: number; y: number; r: number };
} & (
  | { kind: 'spine'; spine: AssetDescription<unknown> }
  | { kind: 'static'; image: string }
);

/** Source-sized node bounds and label placement for one themed plate. */
export type DreamDriftNodeStyle = {
  width: number;
  height: number;
  label: { x: number; y: number; color: string; r: number };
  flag: { x: number; y: number };
};

/** Images used to render the active Dream Drift pet on home scenes. */
export type DreamDriftHomePetAssets = {
  bed: string;
};

/** Asset and path operations supplied by every Dream Drift presentation theme. */
export type DreamDriftThemeProvider = {
  getAssets: () => ThemeAsset[];
  getHomePetAssets: () => DreamDriftHomePetAssets;
  getHudButtonAssets: () => DreamDriftHudButtonAssets;
  getIntroImagePath: () => string;
  getPuzzleBackground: () => string;
  getMapConfig: () => DreamDriftMapConfig;
  getMapImagePath: (image: string) => string;
  getNodeStyle: (isCheckpoint: boolean) => DreamDriftNodeStyle;
  getNodeImage: (opts: {
    status: DreamDriftNodeStatus;
    isCheckpoint: boolean;
  }) => string;
};

/** Frontend Dream Drift presentation themes currently available to the game. */
const themes: Record<DreamDriftTheme, DreamDriftThemeProvider> = {
  momotaro: dreamDriftThemeMomotaro,
  urashima: dreamDriftThemeUrashima,
  asiatravel: dreamDriftThemeAsiaTravel,
};

/**
 * Get the active Dream Drift presentation theme.
 *
 * @returns Provider exposing the active theme's assets and map configuration.
 */
export function dreamDriftTheme() {
  // Event weeks include the reward-claim gap and change only at Friday midnight in Japan.
  return themes[dreamDriftGetEventTheme(now())];
}
